import { assertBasisPoints, assertPaise, mulDivFloor, mulDivRound } from "./integer";

/**
 * Discounts. BL-07: percent or amount, on the bill or on an item.
 *
 * Offers (Phase 9) resolve to one of these two rules before they reach the
 * calculation: a flat amount off each unit becomes `amount` = flat × quantity,
 * and a fixed price becomes `amount` = line gross − fixed price × quantity.
 */
export type DiscountRule =
	/** A share of the value, in basis points (1000 = 10%), optionally capped. */
	| { type: "percent"; bp: number; maxPaise: number | null }
	/** A fixed amount off the whole line or the whole bill. */
	| { type: "amount"; paise: number };

export interface DiscountInput {
	rule: DiscountRule;
	/** What the bill prints, such as an offer name. Null prints a generic "Discount". */
	label: string | null;
	/** Why staff gave it. Printed nowhere, kept for the record. */
	reason: string | null;
	/** The offer that produced it, when it came from an offer or coupon. */
	offerId: string | null;
}

/**
 * The discount in paise on a value, never more than the value itself.
 * Percent: round(value × bp ÷ 10000), then the cap, then the value.
 * Amount: the amount, or the value if the amount is larger.
 */
export function discountAmount(rule: DiscountRule, valuePaise: number): number {
	assertPaise(valuePaise, "discounted value");
	if (rule.type === "percent") {
		assertBasisPoints(rule.bp, "discount percent");
		let amount = mulDivRound(valuePaise, rule.bp, 10000);
		if (rule.maxPaise !== null) {
			assertPaise(rule.maxPaise, "discount cap");
			amount = Math.min(amount, rule.maxPaise);
		}
		return Math.min(amount, valuePaise);
	}
	assertPaise(rule.paise, "discount amount");
	return Math.min(rule.paise, valuePaise);
}

/**
 * Spreads `amount` over `weights` in proportion, in whole paise, so that the
 * parts sum to `amount` exactly (largest remainder method):
 *
 * 1. Each part gets floor(amount × weight ÷ total weight).
 * 2. The paise still left over, fewer than the number of parts, go one each to
 *    the parts with the largest remainders, the earlier part first on a tie.
 *
 * No part is ever negative or larger than its weight when amount ≤ total weight,
 * and a part with weight 0 gets 0.
 */
export function allocate(amount: number, weights: readonly number[]): number[] {
	assertPaise(amount, "amount to spread");
	let total = 0;
	for (const w of weights) {
		assertPaise(w, "weight");
		total += w;
	}
	if (amount === 0 || total === 0) {
		if (amount > 0) throw new RangeError("Cannot spread an amount over nothing");
		return weights.map(() => 0);
	}
	if (amount > total) throw new RangeError("Cannot spread more than the total");

	const parts = weights.map((w) => mulDivFloor(amount, w, total));
	let left = amount - parts.reduce((s, p) => s + p.quotient, 0);
	const order = parts
		.map((p, index) => ({ index, remainder: p.remainder }))
		.sort((a, b) =>
			a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
		);
	const result = parts.map((p) => p.quotient);
	for (const { index } of order) {
		if (left === 0) break;
		result[index] = (result[index] ?? 0) + 1;
		left -= 1;
	}
	return result;
}
